'use client'

import { useEffect, useRef, useState } from 'react'
import { Fuel, MapPin } from 'lucide-react'

import { useSiteSettings } from '@/lib/site-settings-context'

import { usePhoneClickTracker } from './phone-link'

interface MachineMobilisationData {
  found: boolean
  distance?: number
  charge?: number
  localities?: string
  callout?: string | null
  message?: string
}

export function MachineMobilisationHint({
  postcode,
  hasError,
}: {
  postcode: string
  hasError: boolean
}) {
  const [data, setData] = useState<MachineMobilisationData | null>(null)
  const [loading, setLoading] = useState(false)
  const cache = useRef<Record<string, MachineMobilisationData>>({})
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null)
  const { phoneNumber } = useSiteSettings()
  const trackPhoneClick = usePhoneClickTracker()

  useEffect(() => {
    const trimmed = postcode?.trim() ?? ''

    if (!/^\d{4}$/.test(trimmed)) {
      setData(null)
      return
    }

    if (cache.current[trimmed]) {
      setData(cache.current[trimmed])
      return
    }

    if (debounceRef.current) clearTimeout(debounceRef.current)

    debounceRef.current = setTimeout(async () => {
      setLoading(true)
      try {
        const res = await fetch(`/api/machine-mobilisation?postcode=${trimmed}`)
        if (res.ok) {
          const json = await res.json()
          cache.current[trimmed] = json
          setData(json)
        }
      } catch {
        // Silently fail, the hint is non-critical
      } finally {
        setLoading(false)
      }
    }, 300)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [postcode])

  if (hasError || !data || loading) return null

  // Unknown postcode
  if (!data.found) {
    return (
      <div className="mt-2 rounded-sm border border-[#d8cfe0] bg-[#f0ebf5] px-3 py-2">
        <p className="text-sm text-[#555]">
          <MapPin className="mr-1.5 inline-block size-3.5 align-text-bottom text-[#999]" />
          Not in our usual patch?{' '}
          {phoneNumber ? (
            <a
              href={`tel:${phoneNumber}`}
              className="text-brand-purple font-semibold underline"
              onClick={trackPhoneClick}
            >
              Give us a bell
            </a>
          ) : (
            'Give us a bell'
          )}{' '}
          and we&apos;ll work something out.
        </p>
      </div>
    )
  }

  return (
    <div className="mt-2 rounded-sm border border-[#d8cfe0] bg-[#f0ebf5] px-3 py-2">
      <p className="text-sm text-[#1a1a1a]">
        <Fuel className="text-brand-purple mr-1.5 inline-block size-3.5 align-text-bottom" />
        That&apos;s about{' '}
        <span className="text-brand-purple font-semibold">{data.distance} km from our yard</span>,
        so that&apos;s roughly{' '}
        <span className="text-brand-purple font-semibold">
          ${data.charge} in machine mobilisation
        </span>
        , charged once for the job (not per day). Quotes are still free!
        {data.callout && (
          <>
            {' '}
            <span className="text-[#555]">We know the area well. {data.callout}</span>
          </>
        )}
      </p>
      <p className="mt-1 text-xs text-[#888]">
        Straight-line estimate from your postcode. Your on-site quote is based on actual road
        distance, so the final number may differ.
      </p>
    </div>
  )
}
